import { reactive } from 'vue';
import { fetchStorefront, clean } from '@utils/GraphQLClient';

export const VARIANT_DATA = `fragment variantData on ProductVariant {
  id
  sku
  image {
    altText
    url(transform: {maxWidth: 800})
  }
  compareAtPrice {
    amount
  }
  price {
    amount
  }
  selectedOptions {
    value
    name
  }
  product { id }
  textBadge: metafield(namespace: "acf_pdp", key: "variant_badge_messaging") { value }
  hoverImage: metafield(key: "hover_lifestyle_image", namespace: "acf_pdp") {
    reference {
      ... on MediaImage {
        image {
          url(
            transform: { maxWidth: 800 }
          )
        }
      }
    }
  }
}`;

export const PRODUCT_QUERY = `query ProductCard($handle: String!) {
  product(handle: $handle) {
    title
    id
    onlineStoreUrl
    priceRange{
      minVariantPrice {
        amount
      }
    }
    options(first: 3) {
      name
      values
    }
    variants(first: 1) {
      edges {
        node {
          ...variantData
        }
      }
    }
    tags
    shortDescription: metafield(namespace: "acf_pdp", key: "description_short") {
      value
    }
    fromPrice: metafield(namespace: "acf_pdp", key: "from_price") {
      value
    }
  }
}${VARIANT_DATA}`;

const data = async (handle) => {
  return {
    variables: {
      handle: handle,
    },
    query: PRODUCT_QUERY,
  };
};

export const Options = {
  fixedWidth: '241px',
  gap: '1rem',
  lazyLoad: false,
  pagination: false,
  mediaQuery: 'min',
  perMove: 1,
  easing: 'cubic-bezier(.33, 1, .68, 1)',
  arrows: true,
  classes: {
    arrows: 'splide__arrows product-slider-arrows',
    arrow: 'splide__arrow product-slider-arrow',
    prev: 'splide__arrow--prev prev-arrow',
    next: 'splide__arrow--next next-arrow',
  },
  breakpoints: {
    433: {
      gap: '1.5rem',
      fixedWidth: '348px',
    },
    769: {
      gap: '2.5rem',
      fixedWidth: '452px',
    },
    1025: {
      gap: '2.5rem',
      fixedWidth: '411px',
    },
  },
};

export const FetchCards = async (cards) => {
  const items = reactive([]);
  for await (const card of cards) {
    const type = card?.cardType || 'PRODUCT';
    if (type === 'CONTENT') {
      const item = card;
      items.push(item);
    } else {
      const handle = card.handle ?? '';
      const sku = card.sku ?? '';
      const badgeText = card.badgeText ?? '';
      const badgeAsset = card.badgeAsset ?? '';
      const bundleMessaging = card.bundleMessaging ?? '';
      const customTitle = card.custom?.title ?? '';
      const customDesc = card.custom?.description ?? '';
      const request = await data(handle);
      await fetchStorefront(request)
        .then((response) => {
          if (!response.data.product) return;

          const item = JSON.parse(JSON.stringify(clean(response.data.product)));
          item.cardType = 'PRODUCT';
          item.swatches = item.options.filter((option) => option.name === 'Color').map((option) => option.values)[0] || [];
          item.featuredSku = sku;
          item.handle = handle;
          item.badgeText = badgeText;
          item.badgeAsset = badgeAsset;
          item.bundleMessaging = bundleMessaging;
          item.customTitle = customTitle;
          item.customDesc = customDesc;
          item.edit = false;

          items.push(item);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  }

  return { items };
};

export const FetchCard = async (obj) => {
  try {
    const type = obj?.cardType || 'PRODUCT';
    if (type === 'CONTENT') return false;

    const handle = obj.handle ?? '';
    const sku = obj.sku ?? '';
    const badgeText = obj.badgeText ?? '';
    const badgeAsset = obj.badgeAsset ?? '';
    const customTitle = obj.custom?.title ?? '';
    const customDesc = obj.custom?.description ?? '';
    const request = await data(handle);
    const response = await fetchStorefront(request).then((res) => clean(res.data.product));
    const item = JSON.parse(JSON.stringify(response));
    item.cardType = 'PRODUCT';
    item.swatches = item.options.filter((option) => option.name === 'Color').map((option) => option.values)[0] || [];
    item.featuredSku = sku;
    item.handle = handle;

    item.badgeText = badgeText;
    item.badgeAsset = badgeAsset;
    item.customTitle = customTitle;
    item.customDesc = customDesc;
    item.edit = false;
    item.index = obj.index ?? false;

    if (obj.variant) {
      const variant = item.variants[0];
      variant.id = obj.variant.id;
      variant.image = {
        altText: obj.variant.featured_image.alt,
        url: obj.variant.featured_image.src,
      };
    }

    return { item };
  } catch (err) {
    return { err };
  }
};

export const fetchSwatch = (color) => {
  const swatches = window?.Brooklinen?.product?.swatches || false;

  const matchingSwatch = swatches.filter((swatch) => swatch.name === color);

  if (matchingSwatch.length <= 0) return false;

  const swatch = matchingSwatch[0].value;
  const isHex = swatch[0] === '#';

  const style = isHex ? `background: ${swatch}` : `background-image: url(${swatch})`;

  return style;
};
